import React from "react";
import { ArrowLeft2, Notification, Profile, Setting2 } from "iconsax-react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { useAuth } from "../../hooks/auth";
import {
  getProfileDisplayName,
  getProfilePhotoSrc,
} from "../../utils/profilePhoto";

const HeaderWrapper = styled.header`
  width: 100%;
  background: ${({ theme }) => theme.colors.card};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lineInWhite};
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.headerFooterInnerMaxPx}px;
  margin: 0 auto;
  padding: 0 max(1.2rem, env(safe-area-inset-left)) 0
    max(1.2rem, env(safe-area-inset-right));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Avatar = styled.div<{ $src?: string; $size?: "sm" | "lg" }>`
  width: ${({ $size }) => ($size === "lg" ? "56px" : "36px")};
  height: ${({ $size }) => ($size === "lg" ? "56px" : "36px")};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  background-image: ${(props) => (props.$src ? `url(${props.$src})` : "none")};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ $size, theme }) =>
    $size === "lg"
      ? `3px solid ${theme.colors.success}`
      : `2px solid ${theme.colors.surface}`};
  box-shadow: ${({ theme }) => theme.shadows.headerAvatar};
  cursor: pointer;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.headerAvatarHover};
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.6rem 0;

  @media (min-width: 768px) {
    padding: 2rem 0;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  min-width: 0;
  flex: 1;
`;

const ProfileTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  min-width: 0;
`;

const ProfileTextCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const GreetingLabel = styled.span`
  font-size: 14px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: var(--font-weight-medium);
  font-family: ${({ theme }) => theme.font.familyHeading};
`;

const GreetingName = styled.strong`
  font-size: 24px;
  line-height: 41px;
  color: ${({ theme }) => theme.colors.textTitle};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-family: ${({ theme }) => theme.font.familyHeading};
`;

const BackBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px;
  margin-left: -8px;
  border-radius: 50%;
  transition: background 0.2s;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.hover};
  }
`;

const TitleText = styled.h1`
  font-size: 24px;
  line-height: 41px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0;
  font-family: ${({ theme }) => theme.font.familyHeading};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  flex-shrink: 0;
`;

const NotifBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
`;

const ExtraContent = styled.div`
  padding: 0 0 1.6rem;
  width: 100%;

  @media (min-width: 768px) {
    padding-bottom: 2rem;
  }
`;

const DescriptionText = styled.p`
  font-size: 14px;
  line-height: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.body};
  color: ${({ theme }) => theme.colors.textComplement};
  margin: 0 0 1.6rem 0;
`;

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  type?: "public" | "private" | "manager" | "dashboard";
  settingsHref?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description = "",
  children = null,
  type = "private",
  settingsHref,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const showBackButton = type !== "public";
  const photoSrc = getProfilePhotoSrc(user);
  const firstName = getProfileDisplayName(user).split(/\s+/)[0] || "Estudante";

  const goNotifications = () => {
    if (user?.type === "admin" || user?.isAdmin) {
      navigate("/notifications");
    } else {
      navigate("/notificacoes");
    }
  };

  return (
    <HeaderWrapper>
      <HeaderInner>
        <TopRow>
          <TitleGroup>
            {type === "dashboard" ? (
              <ProfileTrigger
                type="button"
                onClick={() => navigate("/profile")}
              >
                <Avatar $src={photoSrc} $size="lg">
                  {!photoSrc && (
                    <Profile
                      size={28}
                      color={
                        user?.uid
                          ? theme.colors.primary
                          : theme.colors.textComplement
                      }
                      variant="Bold"
                    />
                  )}
                </Avatar>
                <ProfileTextCol>
                  <GreetingLabel>Olá</GreetingLabel>
                  <GreetingName>{firstName}</GreetingName>
                </ProfileTextCol>
              </ProfileTrigger>
            ) : (
              <>
                {showBackButton && (
                  <BackBtn onClick={() => navigate(-1)} aria-label="Voltar">
                    <ArrowLeft2 size={24} color="currentColor" />
                  </BackBtn>
                )}
                <TitleText>{title}</TitleText>
              </>
            )}
          </TitleGroup>

          <RightControls>
            {user && (
              <>
                {settingsHref && (
                  <NotifBtn
                    type="button"
                    onClick={() => navigate(settingsHref)}
                    aria-label="Configurações"
                  >
                    <Setting2 size={24} color="currentColor" variant="Linear" />
                  </NotifBtn>
                )}
                <NotifBtn
                  type="button"
                  onClick={goNotifications}
                  aria-label="Notificações"
                >
                  <Notification
                    size={24}
                    color="currentColor"
                    variant="Linear"
                  />
                </NotifBtn>
                {type !== "dashboard" && (
                  <Avatar
                    $src={user.avatar}
                    onClick={() => navigate("/profile")}
                    title="Meu Perfil"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/profile");
                      }
                    }}
                  >
                    {!user.avatar && (
                      <Profile
                        size={20}
                        color={theme.colors.textSecondary}
                        variant="Bulk"
                      />
                    )}
                  </Avatar>
                )}
              </>
            )}
          </RightControls>
        </TopRow>

        {type !== "dashboard" && (description || children) && (
          <ExtraContent>
            {description && <DescriptionText>{description}</DescriptionText>}
            {children}
          </ExtraContent>
        )}
      </HeaderInner>
    </HeaderWrapper>
  );
};

export default PageHeader;
